/**
 * Comfort (ADR 0032): the theme and the switches that make Drang easier to look at belong to the person — the same
 * on every device and in every workspace, kept with the profile on the server. `useSettings` lays them over the
 * workspace settings, so widgets keep reading `settings.a11y.*`.
 */
import { useCallback } from "react";
import { useSession } from "./session";

export interface Comfort {
  theme: "system" | "light" | "dark";
  reducedMotion: boolean;
  muteBadges: boolean;
  plainColors: boolean;
  /** dotted lines around each dashboard widget, in the middle of the gaps */
  widgetLines: boolean;
  /** the day review counts the open tasks (spec §9.19: off unless the person asks) */
  showOpenCount: boolean;
  /** a quick-add line on every page: in the top bar, at the bottom above the tray, or none */
  quickAddBar: "off" | "top" | "bottom";
}

export const DEFAULT_COMFORT: Comfort = {
  theme: "system",
  reducedMotion: false,
  muteBadges: false,
  plainColors: false,
  widgetLines: false,
  showOpenCount: false,
  quickAddBar: "off",
};

export function useComfort(): Comfort & { set(changes: Partial<Comfort>): Promise<void> } {
  const comfort = useSession((s) => s.session?.profile?.comfort);
  const set = useCallback(async (changes: Partial<Comfort>) => {
    const apply = (next: Comfort) =>
      useSession.setState((st) =>
        st.session?.profile
          ? { session: { ...st.session, profile: { ...st.session.profile, comfort: next } } }
          : st,
      );
    const before = { ...DEFAULT_COMFORT, ...useSession.getState().session?.profile?.comfort };
    apply({ ...before, ...changes }); // at once; the server confirms or the old value comes back
    const r = await fetch("/api/internal/session/comfort", {
      method: "PUT",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(changes),
    }).catch(() => null);
    if (r?.ok) apply(((await r.json()) as { comfort: Comfort }).comfort);
    else apply(before);
  }, []);
  return { ...DEFAULT_COMFORT, ...comfort, set };
}
